import React from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { motion } from "framer-motion";
import { ArrowRight, CheckCircle2, XCircle } from "lucide-react";
import SEOHead from "@/components/SEOHead";

const ease = [0.16, 1, 0.3, 1];
const fadeUp = (delay = 0) => ({ initial: { opacity: 0, y: 20 }, whileInView: { opacity: 1, y: 0 }, viewport: { once: true, margin: "-50px" }, transition: { duration: 0.6, ease, delay } });
const stagger = (i) => ({ initial: { opacity: 0, y: 24 }, whileInView: { opacity: 1, y: 0 }, viewport: { once: true }, transition: { duration: 0.55, ease, delay: i * 0.1 } });

const ROLES = [
  { title: "Machine Learning Engineering", desc: "ML model development, training pipelines, MLOps, and production ML systems.", tags: ["PyTorch", "TensorFlow", "MLflow", "Kubeflow"] },
  { title: "AI Engineering", desc: "LLM applications, AI agents, RAG systems, fine-tuning, and applied AI products.", tags: ["LangChain", "OpenAI", "Hugging Face", "Vector DBs"] },
  { title: "Software Engineering", desc: "Backend, distributed systems, APIs, cloud infrastructure, and platform engineering.", tags: ["Python", "Go", "Java", "Kubernetes"] },
  { title: "Cybersecurity", desc: "Security engineering, application security, cloud security, and threat modeling.", tags: ["Penetration Testing", "SIEM", "IAM", "Zero Trust"] },
  { title: "Data Engineering", desc: "Data pipelines, warehousing, streaming systems, ETL, and analytics infrastructure.", tags: ["Spark", "dbt", "Airflow", "Snowflake"] },
  { title: "ML Ops & Platform", desc: "CI/CD for ML, model serving, monitoring, feature stores, and ML infrastructure.", tags: ["Seldon", "Feast", "Ray", "Docker"] },
];

const WhoWeHelpPage = () => (
  <div data-testid="who-we-help-page" className="bg-white">
    <SEOHead title="Who We Help | ApplyStrat" description="ApplyStrat serves ML Engineers, AI Engineers, Software Engineers, Cybersecurity professionals, and Data Engineers targeting roles in the US tech market." canonical="https://applystrat.com/who-we-help" />

    <section className="pt-36 pb-16 bg-white text-center" style={{ background: "radial-gradient(circle at 50% -20%, rgba(255,82,0,0.12) 0%, transparent 65%)" }}>
      <div className="max-w-3xl mx-auto px-6">
        <motion.div {...fadeUp()}>
          <p className="section-label-orange mb-4">Eligibility</p>
          <h1 className="text-5xl md:text-6xl font-medium tracking-tighter text-ink mb-6">Who We Help</h1>
          <p className="text-lg text-ink-secondary max-w-xl mx-auto leading-relaxed">We serve a selective group of serious tech candidates. Limited slots mean focused attention for everyone we work with.</p>
        </motion.div>
      </div>
    </section>

    <section className="py-16 bg-white">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <motion.div {...fadeUp()} className="text-center mb-14">
          <h2 className="text-3xl md:text-4xl font-medium tracking-tight text-ink">Tech Roles We Currently Support</h2>
        </motion.div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {ROLES.map((role, i) => (
            <motion.div key={role.title} {...stagger(i)} className="bg-surface-raised rounded-3xl p-7 hover:bg-white hover:shadow-[0_8px_30px_rgb(0,0,0,0.06)] hover:-translate-y-1 transition-all duration-400">
              <h3 className="text-ink font-medium text-base mb-3">{role.title}</h3>
              <p className="text-ink-secondary text-sm leading-relaxed mb-4">{role.desc}</p>
              <div className="flex flex-wrap gap-2">
                {role.tags.map((tag) => (
                  <span key={tag} className="px-3 py-1 rounded-full bg-white border border-black/5 text-ink-secondary text-xs font-medium">{tag}</span>
                ))}
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>

    <section className="py-16 pb-24 bg-surface-raised">
      <div className="max-w-3xl mx-auto px-6">
        <motion.div {...fadeUp()} className="text-center mb-14">
          <h2 className="text-3xl md:text-4xl font-medium tracking-tight text-ink">Who Qualifies</h2>
        </motion.div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <motion.div {...fadeUp(0.1)} className="bg-white rounded-3xl p-8 border border-green-100 shadow-sm">
            <h3 className="text-ink font-medium text-base mb-5 flex items-center gap-2"><CheckCircle2 size={16} className="text-green-500" /> You likely qualify if</h3>
            <ul className="space-y-3">
              {["Valid US work authorization", "Relevant technical experience", "Actively pursuing roles", "Open to sharing preferences upfront", "Serious about a focused approach", "Targeting the US job market"].map((item) => (
                <li key={item} className="flex items-start gap-3"><CheckCircle2 size={13} className="text-green-500 mt-0.5 flex-shrink-0" /><span className="text-ink-secondary text-sm">{item}</span></li>
              ))}
            </ul>
          </motion.div>
          <motion.div {...fadeUp(0.2)} className="bg-white rounded-3xl p-8 border border-red-50 shadow-sm">
            <h3 className="text-ink font-medium text-base mb-5 flex items-center gap-2"><XCircle size={16} className="text-red-400" /> We're not a fit if</h3>
            <ul className="space-y-3">
              {["Non-technical or generalist roles", "Outside the US market", "Passive job seekers", "Not willing to share preferences"].map((item) => (
                <li key={item} className="flex items-start gap-3"><XCircle size={13} className="text-red-400 mt-0.5 flex-shrink-0" /><span className="text-ink-secondary text-sm">{item}</span></li>
              ))}
            </ul>
            <div className="mt-6 pt-5 border-t border-black/5">
              <p className="text-ink-secondary text-xs leading-relaxed">Not sure? Apply anyway — we'll let you know during review.</p>
            </div>
          </motion.div>
        </div>
      </div>
    </section>

    <section className="py-20 bg-white border-t border-black/5">
      <div className="max-w-2xl mx-auto px-6 text-center">
        <motion.div {...fadeUp()}>
          <h2 className="text-3xl font-medium text-ink mb-5">Sound Like You?</h2>
          <p className="text-ink-secondary text-base mb-8">Apply now and we'll confirm eligibility within 24 hours.</p>
          <Button asChild size="lg" className="shadow-sm hover:scale-[1.02]">
            <Link to="/apply" data-testid="who-we-help-apply-button">Apply to Work With Us <ArrowRight size={18} /></Link>
          </Button>

        </motion.div>
      </div>
    </section>
  </div>
);

export default WhoWeHelpPage;
